/**
 * Product reviews & ratings — 3D carousel with drag, autoplay, and infinite loop.
 * @extends HTMLElement
 */
class ProductReviewsRatings extends HTMLElement {
  /** @type {number | null} */
  #animationFrame = null;

  /** @type {ReturnType<typeof setInterval> | null} */
  #autoInterval = null;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #pauseTimeout = null;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #autoPaused = false;

  /** @type {boolean} */
  #isDragging = false;

  /** @type {number} */
  #dragStartX = 0;

  /** @type {number} */
  #dragDiffX = 0;

  /** @type {number} */
  #targetPos = 0;

  /** @type {number} */
  #animatedPos = 0;

  /** @type {number} */
  #realCount = 0;

  /** @type {HTMLElement[]} */
  #cardEls = [];

  /** @type {HTMLButtonElement[]} */
  #dotEls = [];

  /** @type {boolean} */
  #initialized = false;

  connectedCallback() {
    this.#initCarousel();
    document.addEventListener('shopify:section:load', this.#handleSectionLoad);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.#handleSectionLoad);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    this.#destroyCarousel();
  }

  /** @param {Event} event */
  #handleSectionLoad = (event) => {
    const target = event.target;
    if (target instanceof Element && target.contains(this)) {
      this.#destroyCarousel();
      this.#initCarousel();
    }
  };

  /** @param {Event} event */
  #handleBlockSelect = (event) => {
    const target = event.target;
    if (target instanceof Element && (target === this || target.contains(this))) {
      requestAnimationFrame(() => {
        this.#destroyCarousel();
        this.#initCarousel();
      });
    }
  };

  #initCarousel() {
    if (this.#initialized) return;

    const track = this.querySelector('[data-prr-track]');
    const dotsEl = this.querySelector('[data-prr-dots]');
    const prevBtn = this.querySelector('[data-prr-prev]');
    const nextBtn = this.querySelector('[data-prr-next]');

    if (!(track instanceof HTMLElement) || !(dotsEl instanceof HTMLElement)) return;

    const realCount = Number.parseInt(this.getAttribute('data-real-count') || '0', 10);
    const originalCards = [...track.querySelectorAll('[data-prr-card]')];

    if (realCount === 0 || originalCards.length === 0) return;

    this.#realCount = realCount;
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    if (realCount > 1 && !track.hasAttribute('data-prr-cloned')) {
      const prependFragment = document.createDocumentFragment();
      const appendFragment = document.createDocumentFragment();

      for (const card of originalCards) {
        prependFragment.appendChild(card.cloneNode(true));
        appendFragment.appendChild(card.cloneNode(true));
      }

      track.prepend(prependFragment);
      track.append(appendFragment);
      track.setAttribute('data-prr-cloned', '');
    }

    this.#cardEls = [...track.children].filter((child) => child instanceof HTMLElement);
    this.#dotEls = [...dotsEl.querySelectorAll('.product-reviews-ratings__dot')].filter(
      (dot) => dot instanceof HTMLButtonElement
    );

    this.#targetPos = realCount;
    this.#animatedPos = realCount;

    for (const [index, dot] of this.#dotEls.entries()) {
      dot.addEventListener(
        'click',
        () => {
          this.#targetPos = index + this.#realCount;
          this.#pauseAuto();
        },
        { signal }
      );
    }

    if (prevBtn instanceof HTMLButtonElement) {
      prevBtn.addEventListener(
        'click',
        () => {
          this.#targetPos -= 1;
          this.#pauseAuto();
        },
        { signal }
      );
    }

    if (nextBtn instanceof HTMLButtonElement) {
      nextBtn.addEventListener(
        'click',
        () => {
          this.#targetPos += 1;
          this.#pauseAuto();
        },
        { signal }
      );
    }

    track.addEventListener('mouseenter', this.#stopAuto, { signal });
    track.addEventListener('mouseleave', this.#handleMouseLeave, { signal });
    track.addEventListener('mousedown', this.#dragStart, { signal });
    track.addEventListener('touchstart', this.#dragStart, { passive: true, signal });
    track.addEventListener('touchmove', this.#dragMove, { passive: true, signal });
    track.addEventListener('touchend', this.#dragEnd, { signal });

    const onMouseMove = /** @param {MouseEvent} event */ (event) => this.#dragMove(event);
    const onMouseUp = () => this.#dragEnd();
    window.addEventListener('mousemove', onMouseMove, { signal });
    window.addEventListener('mouseup', onMouseUp, { signal });

    this.#render(this.#animatedPos);
    this.#animationFrame = requestAnimationFrame(this.#animate);
    this.#startAuto();

    this.#initialized = true;
  }

  #destroyCarousel() {
    if (this.#animationFrame !== null) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }

    this.#stopAuto();

    if (this.#pauseTimeout !== null) {
      clearTimeout(this.#pauseTimeout);
      this.#pauseTimeout = null;
    }

    if (this.#abortController) {
      this.#abortController.abort();
      this.#abortController = null;
    }

    this.#initialized = false;
    this.#autoPaused = false;
    this.#isDragging = false;
    this.#cardEls = [];
    this.#dotEls = [];
  }

  /** @param {number} pos */
  #render(pos) {
    const realCount = this.#realCount;
    const active = ((Math.round(pos) % realCount) + realCount) % realCount;

    for (const [index, card] of this.#cardEls.entries()) {
      const diff = index - pos;
      const abs = Math.abs(diff);

      card.style.transform = `translateX(${diff * 240}px) translateZ(${-abs * 180}px) rotateY(${diff * -35}deg) scale(${1 - abs * 0.12})`;
      card.style.opacity = abs > 4 ? '0' : String(1 - abs * 0.25);
      card.style.zIndex = String(100 - Math.round(abs));
      card.classList.toggle('active', index % realCount === active);
    }

    for (const [index, dot] of this.#dotEls.entries()) {
      dot.classList.toggle('active', index === active);
      dot.setAttribute('aria-selected', index === active ? 'true' : 'false');
    }
  }

  #animate = () => {
    if (!this.#initialized) return;

    this.#animatedPos += (this.#targetPos - this.#animatedPos) * 0.08;
    this.#render(this.#animatedPos);

    const realCount = this.#realCount;
    if (this.#animatedPos >= realCount * 2) {
      this.#animatedPos -= realCount;
      this.#targetPos -= realCount;
    } else if (this.#animatedPos < realCount) {
      this.#animatedPos += realCount;
      this.#targetPos += realCount;
    }

    this.#animationFrame = requestAnimationFrame(this.#animate);
  };

  #startAuto() {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (this.#realCount <= 1 || prefersReducedMotion || this.#autoPaused) return;

    this.#autoInterval = setInterval(() => {
      this.#targetPos += 1;
    }, 3000);
  }

  #stopAuto = () => {
    if (this.#autoInterval !== null) {
      clearInterval(this.#autoInterval);
      this.#autoInterval = null;
    }
  };

  #handleMouseLeave = () => {
    if (!this.#autoPaused) {
      this.#startAuto();
    }
  };

  #pauseAuto = () => {
    this.#autoPaused = true;
    this.#stopAuto();

    if (this.#pauseTimeout !== null) {
      clearTimeout(this.#pauseTimeout);
    }

    this.#pauseTimeout = setTimeout(() => {
      this.#autoPaused = false;
      this.#startAuto();
    }, 5000);
  };

  /** @param {MouseEvent | TouchEvent} event */
  #getClientX(event) {
    if (event instanceof TouchEvent && event.touches[0]) {
      return event.touches[0].clientX;
    }

    if (event instanceof MouseEvent) {
      return event.clientX;
    }

    return 0;
  }

  /** @param {MouseEvent | TouchEvent} event */
  #dragStart = (event) => {
    if (this.#realCount <= 1) return;

    this.#isDragging = true;
    this.#dragStartX = this.#getClientX(event);
    this.#stopAuto();
  };

  /** @param {MouseEvent | TouchEvent} event */
  #dragMove = (event) => {
    if (!this.#isDragging) return;

    this.#dragDiffX = this.#getClientX(event) - this.#dragStartX;
    this.#animatedPos = this.#targetPos - this.#dragDiffX / 200;
  };

  #dragEnd = () => {
    if (!this.#isDragging) return;

    this.#isDragging = false;

    if (this.#dragDiffX > 50) {
      this.#targetPos -= 1;
    } else if (this.#dragDiffX < -50) {
      this.#targetPos += 1;
    }

    this.#pauseAuto();
    this.#dragDiffX = 0;
  };
}

if (!customElements.get('product-reviews-ratings')) {
  customElements.define('product-reviews-ratings', ProductReviewsRatings);
}
